import { useEffect, useState } from 'react';
import {
    Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
    Table, TableHead, TableRow, TableCell, TableBody, Card, CardContent,
    makeStyles, CircularProgress, Snackbar, IconButton, Chip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import DeleteIcon from '@material-ui/icons/Delete';
import { getTournaments } from '../../data';
import { getAdminSchedule, scrapeAdminSchedule, updateAdminSchedule } from '../../data/admin';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        maxWidth: 1200,
        margin: '0 auto',
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    tableContainer: {
        overflowX: 'auto',
        marginTop: theme.spacing(2),
        maxHeight: 500,
        overflow: 'auto',
    },
    gwRow: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        flexWrap: 'wrap',
    },
    gwLabel: {
        minWidth: 60,
        fontWeight: 600,
    },
}));

export default function ScheduleManager() {
    const classes = useStyles();
    const [tournaments, setTournaments] = useState([]);
    const [tournament, setTournament] = useState('');
    const [schedule, setSchedule] = useState([]);
    const [gameweeks, setGameweeks] = useState({});
    const [loading, setLoading] = useState(false);
    const [scraping, setScraping] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [seriesId, setSeriesId] = useState('');

    // Gameweek editing
    const [newGwName, setNewGwName] = useState('');
    const [newGwMatches, setNewGwMatches] = useState('');

    useEffect(() => {
        (async () => {
            const data = await getTournaments();
            setTournaments(data);
        })();
    }, []);

    const handleLoad = async () => {
        if (!tournament) return;
        setLoading(true);
        try {
            const data = await getAdminSchedule(tournament);
            setSchedule(data.schedule || []);
            setGameweeks(data.gameweeks || {});
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
        setLoading(false);
    };

    const handleScrape = async () => {
        if (!tournament || !seriesId) return;
        setScraping(true);
        try {
            const result = await scrapeAdminSchedule(tournament, seriesId);
            if (result.success) {
                setSchedule(result.data.schedule);
                setSnackbar({ open: true, message: `Scraped ${result.data.schedule.length} matches`, severity: 'success' });
            } else {
                setSnackbar({ open: true, message: result.error, severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
        setScraping(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const result = await updateAdminSchedule(tournament, schedule, gameweeks);
            if (result.success) {
                setSnackbar({ open: true, message: 'Schedule saved successfully', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: result.error, severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
        setSaving(false);
    };

    const handleAddGameweek = () => {
        if (!newGwName) return;
        const matches = newGwMatches.split(',').map(m => m.trim()).filter(Boolean);
        setGameweeks({ ...gameweeks, [newGwName]: matches });
        setNewGwName('');
        setNewGwMatches('');
    };

    const handleRemoveGameweek = (gwName) => {
        const updated = { ...gameweeks };
        delete updated[gwName];
        setGameweeks(updated);
    };

    const handleAutoGameweeks = () => {
        if (schedule.length === 0) return;
        const gws = {};
        const size = 5;
        for (let i = 0; i < schedule.length; i += size) {
            const gwNum = Math.floor(i / size) + 1;
            gws[`gw${gwNum}`] = schedule.slice(i, i + size).map(m => m.index);
        }
        setGameweeks(gws);
    };

    const sortedGwKeys = Object.keys(gameweeks).sort((a, b) => {
        const numA = parseInt(a.replace('gw', ''));
        const numB = parseInt(b.replace('gw', ''));
        return numA - numB;
    });

    const allMatchIndices = schedule.map(m => m.index);
    const coveredIndices = Object.values(gameweeks).flat();
    const uncovered = allMatchIndices.filter(idx => !coveredIndices.includes(idx));

    return (
        <div className={classes.root}>
            <Typography variant="h5" gutterBottom>Schedule Manager</Typography>

            <Card className={classes.section}>
                <CardContent>
                    <div className={classes.formRow}>
                        <FormControl style={{ minWidth: 200 }}>
                            <InputLabel>Tournament</InputLabel>
                            <Select value={tournament} onChange={(e) => setTournament(e.target.value)}>
                                {tournaments.map(t => (
                                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" color="primary" onClick={handleLoad} disabled={!tournament || loading}>
                            {loading ? <CircularProgress size={20} /> : 'Load Existing'}
                        </Button>
                    </div>
                    <div className={classes.formRow}>
                        <TextField
                            label="Series ID (e.g. ipl-2026-1510719)"
                            value={seriesId}
                            onChange={(e) => setSeriesId(e.target.value)}
                            style={{ width: 350 }}
                            size="small"
                            helperText="From the espncricinfo series URL"
                        />
                        <Button variant="contained" color="secondary" onClick={handleScrape}
                            disabled={!tournament || !seriesId || scraping}>
                            {scraping ? <CircularProgress size={20} /> : 'Fetch Schedule'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {schedule.length > 0 && (
                <>
                    <Card className={classes.section}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Matches ({schedule.length})
                            </Typography>
                            <div className={classes.tableContainer}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Teams</TableCell>
                                            <TableCell>UTC Time</TableCell>
                                            <TableCell>Title</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {schedule.map((match, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{match.index}</TableCell>
                                                <TableCell>{(match.teams || []).join(' vs ')}</TableCell>
                                                <TableCell>{match.utcTime}</TableCell>
                                                <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {match.title}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={classes.section}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Gameweeks</Typography>
                            {uncovered.length > 0 && (
                                <Alert severity="warning" style={{ marginBottom: 8 }}>
                                    Matches not in any gameweek: {uncovered.join(', ')}
                                </Alert>
                            )}
                            {sortedGwKeys.map(gw => (
                                <div key={gw} className={classes.gwRow}>
                                    <Typography className={classes.gwLabel}>{gw}</Typography>
                                    {gameweeks[gw].map(idx => (
                                        <Chip key={idx} label={idx} size="small" style={{ margin: 2 }} />
                                    ))}
                                    <IconButton size="small" onClick={() => handleRemoveGameweek(gw)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                            <div className={classes.gwRow} style={{ marginTop: 16 }}>
                                <TextField label="GW Name" value={newGwName} size="small"
                                    onChange={(e) => setNewGwName(e.target.value)}
                                    placeholder="gw14" style={{ width: 100 }} />
                                <TextField label="Match indices (comma-sep)" value={newGwMatches} size="small"
                                    onChange={(e) => setNewGwMatches(e.target.value)}
                                    placeholder="1,2,3,4,5" style={{ width: 200 }} />
                                <Button size="small" variant="outlined" onClick={handleAddGameweek}>Add GW</Button>
                            </div>
                            <Button size="small" variant="outlined" onClick={handleAutoGameweeks}
                                style={{ marginTop: 8 }}>
                                Auto-generate (groups of 5)
                            </Button>
                        </CardContent>
                    </Card>

                    <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Save Schedule & Gameweeks'}
                    </Button>
                </>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
}
